import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import ThumbUp from '@mui/icons-material/ThumbUp'
import ThumbDown from '@mui/icons-material/ThumbDown'
import {
  likeFacebookPostInitiated,
  unlikeFacebookPostInitiated,
} from '../../../../../store/facebook/facebook.actions'

import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import { LikeFacebookPostRequest, UnlikeFacebookPostRequest } from '@3m5/sm_publish-frontend-api'
import { FacebookPost, InstagramPost, NetworkType } from '@3m5/sm_publish-frontend-api'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { PostingRootState } from '../../../../../store/postingRoot.reducer'
import { namedRequestData } from '../../../../../store/common/common.selectors'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    textTransform: 'inherit',
    padding: '9px 16px 6px',
    width: 'auto',
    '& .MuiButton-startIcon>*:nth-of-type(1)': {
      fontSize: '24px',
    },
  },
}))

type IProps = {
  post: FacebookPost | InstagramPost | any
  networkPageId: string
  networkType?: NetworkType
  value?: any
  noDesc?: boolean
}

const Like: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const dispatchLikePost = useCallback((params: LikeFacebookPostRequest) => dispatch(likeFacebookPostInitiated(params)), [dispatch])
  const dispatchUnlikePost = useCallback((params: UnlikeFacebookPostRequest) => dispatch(unlikeFacebookPostInitiated(params)), [dispatch])

  const actionOnLikePost: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, likeFacebookPostInitiated.type))
  const actionOnUnlikePost: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, unlikeFacebookPostInitiated.type))

  const [like, setLike] = React.useState<boolean>(props.value || false)

  const likePost = () => {
    const params: LikeFacebookPostRequest = {
      networkPageId: props.networkPageId,
      networkPostId: props.post.networkPostId,
    }
    dispatchLikePost(params)
  }

  const unlikePost = () => {
    const params: UnlikeFacebookPostRequest = {
      networkPageId: props.networkPageId,
      networkPostId: props.post.networkPostId,
    }
    dispatchUnlikePost(params)
  }

  useEffect(() => {
    actionOnLikePost?.status === 'success' && actionOnLikePost?.id === props.post.networkPostId && setLike(true)
    actionOnUnlikePost?.status === 'success' && actionOnUnlikePost?.id === props.post.networkPostId && setLike(false)
  }, [actionOnLikePost, actionOnUnlikePost])

  return (
    <ButtonComponent
      text={like ? t('networkContentDashboard:unlikePost') : t('networkContentDashboard:likePost')}
      startIcon={like ? <ThumbDown /> : <ThumbUp />}
      className={classes.root}
      variant='text'
      disabled={(actionOnLikePost?.inProgress && actionOnLikePost?.id === props.post.networkPostId) || (actionOnUnlikePost?.inProgress && actionOnUnlikePost?.id === props.post.networkPostId)}
      color='info'
      onClick={() => {
        like ? unlikePost() : likePost()
      }}
    />
  )
}

export default Like
